import { useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import OtpInput from 'react-otp-input'
import { Link, useNavigate } from 'react-router-dom'

import { Button, TextInput } from 'components/common'
import {
  ERROR_CODES,
  PAGE_DASHBOARD,
  PAGE_FORGOT_PASSWORD,
} from 'utils/constants'
import { userLogin, verifyOtp } from 'utils/api/auth'
import { loginSchema } from 'utils/schema/authSchema'
import { handleError } from 'utils/helpers/handleError'
import { useAuth } from 'hooks'

export type LoginFormData = {
  email: string
  password: string
}

interface OtpFormData {
  otp: string
}

type LoginStep = 'credentials' | 'otp'

interface OtpInfo {
  email: string
  managerName?: string
}

const LoginForm = () => {
  const navigate = useNavigate()

  const [step, setStep] = useState<LoginStep>('credentials')
  const [otpInfo, setOtpInfo] = useState<OtpInfo | null>(null)
  const { loginUser } = useAuth()

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
  })

  const {
    handleSubmit: handleOtpSubmit,
    setValue: setOtpValue,
    watch: watchOtp,
    formState: { errors: otpErrors, isSubmitting: isOtpSubmitting },
    clearErrors: clearOtpErrors,
    reset: resetOtp,
  } = useForm<OtpFormData>({
    mode: 'onChange',
    defaultValues: { otp: '' },
  })

  const otp = watchOtp('otp')
  const isOtpStep = step === 'otp'

  const onLogin = async (userData: LoginFormData) => {
    const loader = toast.loading('Verifying credentials...')
    try {
      const response = await userLogin(userData)
      // Batch both state updates together to avoid intermediate renders
      setOtpInfo({
        email: userData.email,
        managerName: response?.data?.managerName,
      })
      setStep('otp')
      toast.success('OTP sent to your approval manager', { id: loader })
    } catch (error) {
      handleError(error, loader, {
        default: 'Something went wrong. Please try again.',
        [ERROR_CODES.DISABLED_ACCOUNT]: 'Your account has been disabled',
        [ERROR_CODES.NOT_FOUND]: 'Invalid email or password',
        [ERROR_CODES.WRONG_PASSWORD]: 'Invalid email or password',
      })
    }
  }

  const onVerifyOtp = async ({ otp }: OtpFormData) => {
    const loader = toast.loading('Verifying OTP...')
    try {
      const { data } = await verifyOtp({ email: otpInfo!.email, otp })

      if (data?.user) {
        loginUser(data.user)
      }

      localStorage.clear()
      navigate(PAGE_DASHBOARD.path)
      toast.success('Login successful', { id: loader })
    } catch (error: any) {
      handleError(error, loader)
    }
  }

  const handleOtpChange = (value: string) => {
    if (/^\d*$/.test(value)) {
      setOtpValue('otp', value)
      clearOtpErrors('otp')
    }
  }

  const handleBack = () => {
    resetOtp()
    setOtpInfo(null)
    setStep('credentials')
  }

  return (
    <section className="w-full max-w-lg p-10">
      {/* ── Credentials step ────────────────────────────────────────────── */}
      {!isOtpStep && (
        <>
          <div className="text-center">
            <h1 className="mb-1 text-4xl font-bold">Welcome Back</h1>
            <h3 className="text-base">Please enter your login details</h3>
          </div>

          <div className="mt-10 ">
            <form onSubmit={handleSubmit(onLogin)}>
              <div className="flex flex-col gap-2">
                <TextInput
                  required
                  type="text"
                  name="email"
                  label="Email"
                  placeholder="Enter your email"
                  register={register}
                  error={errors?.email?.message}
                />
                <TextInput
                  required
                  register={register}
                  name="password"
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  error={errors?.password?.message}
                />
              </div>
              <Button
                buttonText={isSubmitting ? 'Signing in...' : 'Continue'}
                className="mt-5 w-full"
                disabled={isSubmitting}
              />
            </form>
            <Link
              to={PAGE_FORGOT_PASSWORD.path}
              className="mt-4 flex text-sm text-gray-600"
            >
              <div className="flex items-center gap-1">Forgot Password?</div>
            </Link>
          </div>
        </>
      )}

      {/* ── OTP step ────────────────────────────────────────────────────── */}
      {isOtpStep && (
        <>
          <div className="mb-6">
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
              Approval required
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              A 6-digit code was sent to your approval manager
            </p>
          </div>

          {/* Info banner */}
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-900" />
            <div>
              <p className="text-sm font-medium text-gray-800">
                {otpInfo?.managerName
                  ? `OTP sent to ${otpInfo.managerName}`
                  : 'OTP sent to your approval manager'}
              </p>
              <p className="mt-0.5 text-xs text-gray-500">
                Contact them to get the verification code
              </p>
            </div>
          </div>

          <form
            onSubmit={handleOtpSubmit(onVerifyOtp)}
            className="flex flex-col gap-6"
            autoComplete="off"
          >
            <div className="flex flex-col gap-3">
              <label className="text-center text-sm font-medium text-gray-700">
                Enter verification code
              </label>

              <div className="flex justify-center">
                <OtpInput
                  renderInput={(props) => (
                    <input {...props} autoComplete="one-time-code" />
                  )}
                  inputType="tel"
                  value={otp}
                  onChange={handleOtpChange}
                  numInputs={6}
                  shouldAutoFocus
                  renderSeparator={<span className="mx-1" />}
                  inputStyle={{
                    width: '46px',
                    height: '52px',
                    margin: '0 3px',
                    fontSize: '20px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    border: `1.5px solid ${
                      otpErrors.otp ? '#ef4444' : '#d1d5db'
                    }`,
                    backgroundColor: otpErrors.otp ? '#fef2f2' : '#ffffff',
                    color: '#111827',
                    textAlign: 'center' as const,
                    outline: 'none',
                    transition:
                      'border-color 0.15s ease, box-shadow 0.15s ease',
                    boxShadow: otpErrors.otp
                      ? '0 0 0 3px rgba(239,68,68,0.10)'
                      : 'none',
                  }}
                />
              </div>

              {otpErrors.otp && (
                <p className="text-center text-xs text-red-500">
                  {otpErrors.otp.message}
                </p>
              )}
            </div>

            <Button
              buttonText={isOtpSubmitting ? 'Verifying...' : 'Verify & sign in'}
              className="w-full"
              disabled={isOtpSubmitting || otp.length !== 6}
            />

            {/* Footer */}
            <div className="border-t border-gray-100 pt-4 text-center">
              <p className="mb-3 text-xs text-gray-400">
                Signed in as{' '}
                <span className="font-medium text-gray-600">
                  {getValues('email')}
                </span>
              </p>
              <button
                type="button"
                onClick={handleBack}
                disabled={isOtpSubmitting}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                >
                  <path d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to login
              </button>
            </div>
          </form>
        </>
      )}
    </section>
  )
}

export default LoginForm
